import { inject, Injectable } from '@angular/core';
import { StateContext } from '@ngxs/store';
import { TableStateModel } from '../table.state.model';
import { Pagination } from '@core/models/pagination.model';
import { ProductsService } from '@core/services/products.service';
import { TableActions } from '../table.actions';
import { NotificationFacade } from '@core/facades/notification.facade';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { MutualFundService } from '@core/services/mutual-fund.service';

@Injectable({
  providedIn: 'root',
})
export class MFProductHandler {
  constructor() {}
  productService = inject(ProductsService);
  notify = inject(NotificationFacade);
  mutualFundService = inject(MutualFundService);

  handleMFProductLoad(ctx: StateContext<TableStateModel>, params: Pagination) {
    this.productService.getMFDProducts({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'mutualFunds',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'mutualFunds',
            error: error.message,
          })
        );
      },
    });
  }

  updateStatus(
    ctx: StateContext<TableStateModel>,
    action: TableActions.UpdateMFProductStatus
  ): Observable<any> {
    const { productId, status, tableKey } = action.payload;
    return this.productService
      .updateStatus({ product_id: productId, status })
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const table = state[tableKey];

          if (!table || !Array.isArray(table.data)) {
            throw new Error(
              `Invalid tableKey or missing data array for table: ${tableKey}`
            );
          }

          const updatedData = table.data.map((product: any) =>
            product.id === productId ? { ...product, status } : product
          );

          ctx.patchState({
            [tableKey]: {
              ...table,
              data: updatedData,
            },
          } as Partial<TableStateModel>);

          this.notify.success('Product status updated successfully');
        }),
        catchError((error) => {
          this.notify.error('Product status update failed:', error);
          return throwError(() => error);
        })
      );
  }

  handleBenchmarkFilter(ctx: StateContext<TableStateModel>, action: TableActions.FilterBenchmarkList) {
    this.mutualFundService.filterBenchmarkData(action.payload.filter).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'benchmark',
            data: response.data?.list,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'benchmark',
            error: error.message,
          })
        );
      },
    });
  }

  handleBenchmarkLoad(ctx: StateContext<TableStateModel>, params: Pagination) {
    this.mutualFundService.getBenchmarkData({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'benchmark',
            data: response.data?.list,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'benchmark',
            error: error.message,
          })
        );
      },
    });
  }
}
